/**
 * Add one team member, in one dialog.
 *
 * The dialog it replaces asked for an email and some roles, sent an invitation,
 * and left clinic roles, visit types, employment type and the Roger App account
 * to a second screen. Anything missed there produced somebody who existed but
 * could not be scheduled, and nothing on the member list said so.
 *
 * So this asks one question first -- what does this person do here -- and derives
 * the rest from the answer, ending in a sentence naming what is about to happen.
 * A grid of checkboxes can be misread; a sentence naming the person and what
 * they will be able to do cannot.
 */

import React, { useEffect } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import type {
  DisciplineType,
  OnboardRogerDocMode,
  UserArchetype,
} from '../../../services/api';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import type { UserType } from '../../../services/api/models/UserType';
import { EMPLOYEE_TYPE_OPTIONS } from '../../../constants/employeeTypes';
import { USER_TYPE_OPTIONS } from '../../../constants/userTypes';
import { disciplineProse } from '../../../constants/disciplines';
import { formatPhone } from '../../../utils/intake';
import type { AgencyClinicResponse } from '../types';
import {
  useIsAgencyRogerEmrEnabled,
  useShowEmrUserManagement,
} from '../../../hooks/useShowEmrUserManagement';
import { ARCHETYPE_OPTIONS } from './archetypes';
import { AdvancedRolesSection } from './AdvancedRolesSection';
import { useOnboardRoleContext } from './useOnboardRoleContext';
import { useOnboardUser, type ExistingAppAccount } from './useOnboardUser';

export interface OnboardUserSubmission {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  archetype: UserArchetype;
  clinic_ids: string[];
  user_types: UserType[];
  disciplines_override: DisciplineType[] | null;
  employee_type: EmployeeType | null;
  employee_type_other: string | null;
  roger_doc: {
    mode: OnboardRogerDocMode;
    username?: string;
    account_id?: string;
  };
  /**
   * Advanced, sent only once edited. Null leaves the server deriving them from
   * the archetype, exactly as it did before Advanced existed.
   */
  agency_role_ids: string[] | null;
  clinic_roles: { role_id: string; clinic_ids: string[] }[] | null;
}

interface OnboardUserDialogProps {
  open: boolean;
  clinics: AgencyClinicResponse[];
  agencyAbbreviation: string | null | undefined;
  /** Every Roger App account in the agency, for spotting an existing one. */
  existingAppAccounts?: readonly ExistingAppAccount[];
  /** Head-count per archetype, shown on the cards. */
  archetypeCounts?: Record<UserArchetype, number>;
  /**
   * Whether this operator may create or link a Roger App account.
   *
   * Creating and linking are gated on `clinician-mgr:access`, which somebody
   * holding only the invite permission does not have. Offering it to them
   * produced a 403 on submit, after they had filled the whole form in.
   */
  allowRogerDocAccounts?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (submission: OnboardUserSubmission) => Promise<void> | void;
}

const FieldError: React.FC<{ message: string }> = ({ message }) => (
  <p className='mt-1 flex items-start gap-1.5 text-xs text-amber-800'>
    <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
    <span>{message}</span>
  </p>
);

export const OnboardUserDialog: React.FC<OnboardUserDialogProps> = ({
  open,
  clinics,
  agencyAbbreviation,
  existingAppAccounts,
  archetypeCounts,
  allowRogerDocAccounts = false,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const showEmr = useShowEmrUserManagement();
  // The agency's own flag, kept apart from the one a super user expands: the
  // Roger App offer below follows the agency, not the operator.
  const isAgencyEmr = useIsAgencyRogerEmrEnabled();
  const roleContext = useOnboardRoleContext(open);
  const state = useOnboardUser({
    clinics,
    agencyAbbreviation,
    existingAppAccounts,
    roles: roleContext.roles,
    effectiveArchetypeRoles: roleContext.effectiveArchetypeRoles,
    showEmrUserManagement: showEmr,
    isAgencyRogerEmrEnabled: isAgencyEmr,
  });
  const {
    form,
    patch,
    reset,
    option,
    disciplines,
    uselessTitles,
    username,
    appAccountOffered,
    setArchetype,
    setEmployeeType,
    toggleClinic,
    effectiveClinicIds,
    toggleTitle,
    blocker,
    existingAccountMatch,
    linkableAccounts,
    linkExistingAccount,
    confirmSentence,
    toggleAgencyRole,
    toggleClinicForRole,
    selectAllClinicsForRole,
    clearClinicsForRole,
    agencyRoleIds,
    clinicRoleIds,
    agencyRolesSubmission,
    clinicRolesSubmission,
    advancedCount,
  } = state;

  // Cleared whenever it closes, not only when cancelled. The parent closes it
  // directly on success, which bypassed the cancel handler -- so reopening
  // carried the previous person's email, clinics, titles and app-account choice,
  // ready to be sent again under a new name.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    await onSubmit({
      email: form.email.trim(),
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      phone_number: form.phoneNumber.trim(),
      archetype: form.archetype,
      clinic_ids: effectiveClinicIds,
      user_types: form.userTypes,
      disciplines_override: form.disciplinesOverride,
      employee_type: form.employeeType || null,
      employee_type_other: form.employeeTypeOther || null,
      // Hiding the section is not the boundary: an operator who switched
      // archetype after it was offered could still carry a stale mode.
      roger_doc:
        !allowRogerDocAccounts || form.appAccountMode === 'none'
          ? { mode: 'none' }
          : form.appAccountMode === 'create'
            ? { mode: 'create', username }
            : { mode: 'link', account_id: form.appAccountId },
      agency_role_ids: agencyRolesSubmission,
      clinic_roles: clinicRolesSubmission,
    });
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && handleClose()}>
      <DialogContent className='max-h-[90vh] w-full max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Add a team member</DialogTitle>
          <DialogDescription>
            They&apos;ll get an email invite to set up their password.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-5'>
          {/* ── Who ── */}
          <div className='flex flex-col gap-3'>
            <div>
              <Label htmlFor='onboard-email'>Work email</Label>
              <Input
                id='onboard-email'
                inputSize='sm'
                type='email'
                value={form.email}
                disabled={isSubmitting}
                onChange={e => patch({ email: e.target.value })}
              />
              {blocker?.field === 'email' && (
                <FieldError message={blocker.message} />
              )}
            </div>
            <div className='flex flex-col gap-3 sm:flex-row'>
              <div className='flex-1'>
                <Label htmlFor='onboard-first'>First name</Label>
                <Input
                  id='onboard-first'
                  inputSize='sm'
                  value={form.firstName}
                  disabled={isSubmitting}
                  onChange={e => patch({ firstName: e.target.value })}
                />
              </div>
              <div className='flex-1'>
                <Label htmlFor='onboard-last'>Last name</Label>
                <Input
                  id='onboard-last'
                  inputSize='sm'
                  value={form.lastName}
                  disabled={isSubmitting}
                  onChange={e => patch({ lastName: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor='onboard-phone'>Mobile number</Label>
              <Input
                id='onboard-phone'
                inputSize='sm'
                value={form.phoneNumber}
                disabled={isSubmitting}
                onChange={e =>
                  patch({ phoneNumber: formatPhone(e.target.value) })
                }
              />
            </div>
          </div>

          {/* ── What they do ──
              Only asked where the answers mean something. Every archetype here
              is a statement about visits and charts, so an agency that does not
              run the EMR is being asked to choose between three jobs nobody at
              it holds. They get back office, which is the one that describes
              somebody whose access is exactly the roles picked under Advanced. */}
          {showEmr && (
            <div>
              <Label>What do they do here?</Label>
              <div className='mt-1 flex flex-col gap-2'>
                {ARCHETYPE_OPTIONS.map(archetype => {
                  const selected = form.archetype === archetype.value;
                  const count = archetypeCounts?.[archetype.value];
                  return (
                    <button
                      key={archetype.value}
                      type='button'
                      aria-pressed={selected}
                      disabled={isSubmitting}
                      onClick={() => setArchetype(archetype.value)}
                      className={`flex items-start gap-3 rounded-md border p-3 text-left transition-colors ${
                        selected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-input hover:border-primary'
                      }`}
                    >
                      <span
                        className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${
                          selected
                            ? 'border-primary bg-primary'
                            : 'border-input'
                        }`}
                      />
                      <span className='min-w-0 flex-1'>
                        <span className='block text-sm font-medium'>
                          {archetype.label}
                        </span>
                        <span className='block text-xs text-muted-foreground'>
                          {archetype.description}
                        </span>
                      </span>
                      {count !== undefined && (
                        <Badge
                          variant='outline'
                          className='shrink-0 whitespace-nowrap text-xs'
                        >
                          {count} {count === 1 ? 'user' : 'users'}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Where ── */}
          {option.needsClinics && (
            <div>
              <Label>Clinic access</Label>
              {/* Every clinic listed and checked, rather than collapsed behind
                  an "all clinics" box. The archetype's clinic role follows this
                  selection to each clinic, and a grant nobody saw being made is
                  the one an operator does not think to narrow. */}
              <div className='mt-1 flex flex-wrap gap-x-4 gap-y-2 rounded-md border p-3'>
                {clinics.map(clinic => (
                  <label
                    key={clinic.id}
                    className='flex items-center gap-2 text-sm'
                  >
                    <Checkbox
                      checkboxSize='sm'
                      checked={effectiveClinicIds.includes(clinic.id)}
                      disabled={isSubmitting}
                      onCheckedChange={() => toggleClinic(clinic.id)}
                    />
                    <span className='whitespace-nowrap'>{clinic.name}</span>
                  </label>
                ))}
              </div>
              {blocker?.field === 'clinics' && (
                <FieldError message={blocker.message} />
              )}
            </div>
          )}

          {/* ── Titles, and the visit types they imply ── */}
          {option.needsTitles && (
            <div>
              <Label>
                Clinical title{' '}
                <span className='font-normal text-muted-foreground'>
                  — add more than one if they hold several
                </span>
              </Label>
              <div className='mt-1 flex flex-wrap gap-3'>
                {USER_TYPE_OPTIONS.map(title => (
                  <label
                    key={title.value}
                    className='flex items-center gap-2 text-sm'
                  >
                    <Checkbox
                      checkboxSize='sm'
                      checked={form.userTypes.includes(title.value)}
                      disabled={isSubmitting}
                      onCheckedChange={() => toggleTitle(title.value)}
                    />
                    <span className='whitespace-nowrap'>{title.label}</span>
                  </label>
                ))}
              </div>

              {disciplines.length > 0 && (
                <div className='mt-3 flex items-start gap-2 rounded-md border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900'>
                  <Info className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  <span className='flex-1'>
                    Assignable for{' '}
                    <strong>
                      {disciplines.map(disciplineProse).join(' and ')}
                    </strong>{' '}
                    visits — set from their titles.
                  </span>
                  {form.disciplinesOverride !== null && (
                    <button
                      type='button'
                      className='shrink-0 font-medium underline'
                      onClick={() => patch({ disciplinesOverride: null })}
                    >
                      Use titles
                    </button>
                  )}
                </div>
              )}

              {uselessTitles.length > 0 && (
                <p className='mt-2 flex items-start gap-2 text-xs text-amber-800'>
                  <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  <span>
                    {uselessTitles.join(', ')} allow no visit types on their
                    own.
                  </span>
                </p>
              )}
            </div>
          )}

          {/* ── Employment, which decides the app account ── */}
          <div>
            <Label htmlFor='onboard-employment'>Employment type</Label>
            <Select
              value={form.employeeType}
              disabled={isSubmitting}
              onValueChange={value => setEmployeeType(value as EmployeeType)}
            >
              <SelectTrigger id='onboard-employment' selectSize='sm'>
                <SelectValue placeholder='Select employment type' />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYEE_TYPE_OPTIONS.map(employment => (
                  <SelectItem key={employment.value} value={employment.value}>
                    {employment.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Roger App account ── */}
          {appAccountOffered && allowRogerDocAccounts && (
            <div>
              <Label>Roger App account</Label>
              <p className='mt-0.5 text-xs text-muted-foreground'>
                The Roger App uses a different username and password from the
                portal website.
              </p>
              <div className='mt-1 flex flex-col gap-2'>
                <label className='flex items-start gap-2 text-sm'>
                  <input
                    type='radio'
                    className='mt-1'
                    checked={form.appAccountMode === 'create'}
                    disabled={isSubmitting}
                    onChange={() => patch({ appAccountMode: 'create' })}
                  />
                  <span className='flex-1'>
                    <span className='block'>Create a new account</span>
                    {form.appAccountMode === 'create' && (
                      <>
                        <Input
                          inputSize='sm'
                          className='mt-1'
                          aria-label='Roger App username'
                          value={username}
                          disabled={isSubmitting}
                          onChange={e =>
                            patch({
                              username: e.target.value,
                              usernameEdited: true,
                            })
                          }
                        />
                        <span className='mt-1 block text-xs text-muted-foreground'>
                          Filled in from their name and your agency code. Change
                          it if you need to.
                        </span>
                      </>
                    )}
                  </span>
                </label>
                {existingAccountMatch && (
                  <div className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
                    <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                    <span>
                      {existingAccountMatch.linkedToEmail ? (
                        <>
                          A similar account,{' '}
                          <strong>
                            {existingAccountMatch.account.username}
                          </strong>
                          , is already linked to{' '}
                          <strong>{existingAccountMatch.linkedToEmail}</strong>{' '}
                          — same person? If so, unlink it from them first. If
                          not, edit the username above — this one is taken.
                        </>
                      ) : (
                        <>
                          <strong>
                            {existingAccountMatch.account.first_name}{' '}
                            {existingAccountMatch.account.last_name}
                          </strong>{' '}
                          already has a Roger App account (
                          <strong>
                            {existingAccountMatch.account.username}
                          </strong>
                          ).{' '}
                          <button
                            type='button'
                            className='font-medium underline'
                            onClick={() =>
                              linkExistingAccount(
                                existingAccountMatch.account.id
                              )
                            }
                          >
                            Link that one instead
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                )}
                <label className='flex items-start gap-2 text-sm'>
                  <input
                    type='radio'
                    className='mt-1'
                    checked={form.appAccountMode === 'link'}
                    disabled={isSubmitting || linkableAccounts.length === 0}
                    onChange={() => patch({ appAccountMode: 'link' })}
                  />
                  <span className='flex-1'>
                    <span className='block'>
                      They already have one — link it
                    </span>
                    {linkableAccounts.length === 0 ? (
                      <span className='mt-0.5 block text-xs text-muted-foreground'>
                        {(existingAppAccounts ?? []).length > 0
                          ? 'Every Roger App account in this agency is already linked to somebody.'
                          : 'No Roger App accounts in this agency yet.'}
                      </span>
                    ) : (
                      form.appAccountMode === 'link' && (
                        <>
                          <Select
                            value={form.appAccountId}
                            disabled={isSubmitting}
                            onValueChange={linkExistingAccount}
                          >
                            <SelectTrigger
                              selectSize='sm'
                              className='mt-1'
                              aria-label='Roger App account to link'
                            >
                              <SelectValue placeholder='Select an account…' />
                            </SelectTrigger>
                            <SelectContent>
                              {(existingAppAccounts ?? []).map(account => (
                                <SelectItem
                                  key={account.id}
                                  value={account.id}
                                  disabled={!!account.linkedToEmail}
                                >
                                  {account.username}
                                  {account.first_name || account.last_name
                                    ? ` — ${[account.first_name, account.last_name].filter(Boolean).join(' ')}`
                                    : ''}
                                  {account.linkedToEmail
                                    ? ` (linked to ${account.linkedToEmail})`
                                    : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className='mt-1 block text-xs text-muted-foreground'>
                            Accounts already linked are shown but cannot be
                            chosen — re-pointing one would break that
                            person&apos;s app login. Unlink it from them first.
                          </span>
                        </>
                      )
                    )}
                  </span>
                </label>
                <label className='flex items-start gap-2 text-sm'>
                  <input
                    type='radio'
                    className='mt-1'
                    checked={form.appAccountMode === 'none'}
                    disabled={isSubmitting}
                    onChange={() => patch({ appAccountMode: 'none' })}
                  />
                  <span>No Roger App account</span>
                </label>
                {blocker?.field === 'appAccount' && (
                  <FieldError message={blocker.message} />
                )}
              </div>
            </div>
          )}

          {/* ── Anything beyond the archetype ── */}
          <AdvancedRolesSection
            clinics={clinics}
            roles={roleContext.roles}
            isLoadingRoles={roleContext.isLoading}
            agencyRoleIds={agencyRoleIds}
            clinicRoleIds={clinicRoleIds}
            count={advancedCount}
            disabled={isSubmitting}
            openInitially={option.picksAgencyRoles}
            problem={
              blocker?.field === 'agencyRoles' ? blocker.message : undefined
            }
            onToggleAgencyRole={toggleAgencyRole}
            onToggleClinicForRole={toggleClinicForRole}
            onSelectAllClinicsForRole={selectAllClinicsForRole}
            onClearClinicsForRole={clearClinicsForRole}
          />

          {/* ── What is about to happen ── */}
          <div className='rounded-md border border-primary/30 bg-primary/5 p-3'>
            <p className='text-xs font-semibold uppercase tracking-wide text-primary'>
              Before you send
            </p>
            <p className='mt-1 text-sm'>{confirmSentence}</p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || blocker !== null}
          >
            {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardUserDialog;
